import {
  db,
  collection,
  addDoc,
  serverTimestamp,
  auth,
  query,
  where,
  getDocs,
  doc,
  getDoc,
  setDoc,
  storage,
  storageRef,
  uploadBytes,
  getDownloadURL,
} from "@/firebase";
import type { SummaryResult } from "@/services/geminiService";
import { logger, logDebug, logInfo, logError, logSuccess, logSecurity } from "@/utils/secureLogger";

export interface SummaryRecord {
  summary_id?: string;
  session_id: string;
  user_id: string;
  userName: string;
  summary_text: string;
  date_generated: any; // Firestore timestamp
  ai_model_used: string;
  source_type: "pdf" | "txt" | "docx" | "note";
  input_length: number;
  subject: string;
  // Additional fields from SummaryResult
  key_points: string[];
  topic: string;
  sources: string[]; // Credible sources used for content generation
  filename?: string; // Original filename/source name
  student_notes?: string; // Original student notes/content
  student_notes_url?: string; // Firebase Storage URL for uploaded files
}

class SummaryService {
  private summariesCollection = collection(db, "summaries");

  /**
   * Upload student notes file to Firebase Storage
   */
  private async uploadStudentNotesToStorage(file: File, userId: string): Promise<string> {
    try {
      logInfo("Starting file upload to Firebase Storage", {
        fileName: file.name,
        fileSize: file.size,
      });

      // Create unique filename with timestamp
      const timestamp = Date.now();
      const sanitizedFileName = file.name.replace(/[^a-zA-Z0-9._-]/g, "_");
      const storagePath = `student_notes/${userId}/${timestamp}_${sanitizedFileName}`;

      // Create storage reference
      const fileRef = storageRef(storage, storagePath);

      // Upload file
      logDebug("Uploading file to Storage", { path: storagePath });
      const uploadResult = await uploadBytes(fileRef, file);
      logSuccess("File uploaded successfully", { path: storagePath });

      // Get download URL
      const downloadURL = await getDownloadURL(uploadResult.ref);
      logSuccess("Download URL retrieved", { url: downloadURL.substring(0, 50) + "..." });

      return downloadURL;
    } catch (error) {
      logError("Error uploading file to Storage", error);
      throw new Error(
        `Failed to upload file: ${error instanceof Error ? error.message : "Unknown error"}`
      );
    }
  }

  /**
   * Create a basic user document for development/testing purposes
   */
  private async createBasicUserDocument(userId: string, email: string): Promise<void> {
    try {
      logDebug("Creating basic user document for development purposes");

      const userData = {
        uid: userId,
        firstName: "Test",
        lastName: "Student",
        email: email,
        userType: "student",
        isEmailVerified: true,
        isVerified: true,
        registrationCompleted: true,
        createdAt: new Date(),
        updatedAt: new Date(),
        subjects: ["Mathematics", "Science", "English"], // Default subjects
        // Add other required fields as needed
      };

      await setDoc(doc(db, "users", userId), userData);
      logSuccess("Basic user document created successfully");
    } catch (error) {
      logError("Error creating basic user document", error);
      throw error;
    }
  }

  /**
   * Get current authenticated student user information
   * @throws {Error} If user is not authenticated or not a student
   */
  private async getCurrentStudentUser(): Promise<{ userId: string; userName: string }> {
    try {
      logDebug("Starting user authentication validation");
      const currentUser = auth.currentUser;
      if (!currentUser) {
        logError("No authenticated user found");
        throw new Error("No authenticated user found - please log in again");
      }

      logSecurity("User authentication verified", "getCurrentStudentUser");
      logDebug("Fetching user document from database");

      // Get user data from Firestore
      const userDocRef = doc(db, "users", currentUser.uid);
      const userDoc = await getDoc(userDocRef);

      if (!userDoc.exists()) {
        logInfo("User document not found, creating basic profile");

        // Create a basic user document for development purposes
        await this.createBasicUserDocument(
          currentUser.uid,
          currentUser.email || "test@example.com"
        );

        // Retry getting the user document
        const retryUserDoc = await getDoc(userDocRef);
        if (!retryUserDoc.exists()) {
          logError("Failed to create user document");
          throw new Error(`Unable to create user profile. Please contact support.`);
        }

        const userData = retryUserDoc.data();
        logDebug("Created and retrieved user data", { userType: userData.userType });

        // Construct user name
        const firstName = userData.firstName || "";
        const lastName = userData.lastName || "";
        const userName = `${firstName} ${lastName}`.trim() || "Test Student";

        logSuccess("User validated successfully (created)");

        return {
          userId: currentUser.uid,
          userName,
        };
      }

      const userData = userDoc.data();
      logDebug("User data retrieved", { userType: userData.userType });

      // Verify user is a student or validator
      if (userData.userType !== "student" && userData.userType !== "validator") {
        logError("User is not a student or validator");
        throw new Error(
          `Only students and validators can save AI-generated summaries. Your account type: ${userData.userType}`
        );
      }

      // Construct user name
      const firstName = userData.firstName || "";
      const lastName = userData.lastName || "";
      const userName = `${firstName} ${lastName}`.trim() || "Unknown User";

      logSuccess("User validated successfully");

      return {
        userId: currentUser.uid,
        userName,
      };
    } catch (error) {
      logError("❌ CRITICAL ERROR: Unable to get current student user", error);
      console.error("🚨 User authentication error details:", error);
      // ✅ FIX: Throw error instead of returning null to propagate to caller
      throw new Error(
        `User authentication failed: ${error instanceof Error ? error.message : "Unknown error"}`
      );
    }
  }

  /**
   * Get active subjects from Firebase subjects collection
   */
  private async getActiveSubjects(): Promise<string[]> {
    try {
      const subjectsQuery = query(collection(db, "subjects"), where("status", "==", "active"));

      const subjectsSnapshot = await getDocs(subjectsQuery);
      const subjects: string[] = [];

      subjectsSnapshot.forEach((doc) => {
        const data = doc.data();
        if (data.name) {
          subjects.push(data.name);
        }
      });

      return subjects;
    } catch (error) {
      logError("Error fetching active subjects", error);
      return [];
    }
  }

  /**
   * Find the best matching subject from active subjects based on AI analysis
   */
  private findBestMatchingSubject(aiSubject: string, activeSubjects: string[]): string {
    if (!aiSubject || activeSubjects.length === 0) {
      return "General"; // Default fallback
    }

    const aiSubjectLower = aiSubject.toLowerCase();

    // First, try exact match
    for (const subject of activeSubjects) {
      if (subject.toLowerCase() === aiSubjectLower) {
        return subject;
      }
    }

    // Then try partial matches
    for (const subject of activeSubjects) {
      const subjectLower = subject.toLowerCase();
      if (aiSubjectLower.includes(subjectLower) || subjectLower.includes(aiSubjectLower)) {
        return subject;
      }
    }

    // Try common subject mappings
    const subjectMappings: Record<string, string[]> = {
      Mathematics: ["math", "algebra", "geometry", "calculus", "statistics", "trigonometry"],
      Science: ["biology", "chemistry", "physics", "earth science", "environmental science"],
      English: ["literature", "writing", "grammar", "reading", "english language"],
      History: ["world history", "american history", "social studies", "historical"],
      "Computer Science": ["programming", "coding", "computer", "software", "technology"],
      Arts: ["art", "music", "drama", "theater", "visual arts", "performing arts"],
      Language: ["spanish", "french", "german", "chinese", "japanese", "foreign language"],
    };

    for (const [subject, keywords] of Object.entries(subjectMappings)) {
      // Check if this subject exists in active subjects
      const matchingSubject = activeSubjects.find((s) => s.toLowerCase() === subject.toLowerCase());
      if (matchingSubject && keywords.some((keyword) => aiSubjectLower.includes(keyword))) {
        return matchingSubject;
      }
    }

    // If no match found, return the first active subject or 'General'
    return activeSubjects[0] || "General";
  }

  /**
   * Save AI-generated summary to Firebase
   */
  async saveSummary(
    summaryResult: SummaryResult,
    sourceFileName: string | null,
    originalContent: string,
    sourceType: "pdf" | "txt" | "docx" | "note" = "note",
    uploadedFile: File | null = null,
    sessionId?: string // ✅ NEW: Optional session ID from completed session
  ): Promise<string> {
    try {
      logger.time("Firebase Summary Save Process");
      logInfo("Starting summary save process");

      // Wait for auth state to be ready if needed
      await this.waitForAuthState();

      // Get current student user information (will throw if authentication fails)
      const userInfo = await this.getCurrentStudentUser();

      logInfo("✅ User authentication validated", {
        userId: userInfo.userId.substring(0, 8) + "***",
        userName: userInfo.userName,
      });

      // Upload file to Storage if provided
      let studentNotesUrl: string | undefined;
      if (uploadedFile) {
        logInfo("File detected, uploading to Storage");
        studentNotesUrl = await this.uploadStudentNotesToStorage(uploadedFile, userInfo.userId);
        logSuccess("File uploaded successfully");
      }

      // Get active subjects for matching
      const activeSubjects = await this.getActiveSubjects();
      logDebug("Active subjects retrieved", { count: activeSubjects.length });

      // ✅ NEW: If sessionId is provided, fetch session data from Firebase
      let sessionSubject: string | null = null;
      if (sessionId) {
        logInfo("Session ID provided - fetching session data from Firebase", { sessionId });
        try {
          const sessionDoc = await getDoc(doc(db, "sessions", sessionId));
          if (sessionDoc.exists()) {
            const sessionData = sessionDoc.data();
            sessionSubject = sessionData.subject || null;
            logSuccess("Session data fetched successfully", {
              sessionId,
              sessionSubject,
              tutorId: sessionData.tutorId,
            });
          } else {
            logError("Session document not found in Firebase", { sessionId });
          }
        } catch (error) {
          logError("Error fetching session data from Firebase", error);
        }
      }

      // Match subject to active subjects (only if no session subject)
      const matchedSubject = sessionSubject
        ? sessionSubject
        : this.findBestMatchingSubject(summaryResult.subject, activeSubjects);

      logDebug("Subject determination completed", {
        aiSubject: summaryResult.subject,
        sessionSubject,
        matchedSubject,
        source: sessionSubject ? "Firebase session" : "AI detection + matching",
      });

      // Use provided session ID or generate new one
      const finalSessionId =
        sessionId || `session_${Date.now()}_${userInfo.userId.substring(0, 8)}`;

      if (sessionId) {
        logInfo("Using provided session ID from completed session", { sessionId });
      }

      // Determine source type based on filename if provided
      let determinedSourceType = sourceType;
      if (sourceFileName) {
        const extension = sourceFileName.toLowerCase().split(".").pop();
        if (extension === "pdf") determinedSourceType = "pdf";
        else if (extension === "txt") determinedSourceType = "txt";
        else if (extension === "docx") determinedSourceType = "docx";
      }

      // Prepare summary record
      const summaryRecord: Omit<SummaryRecord, "summary_id"> = {
        session_id: finalSessionId,
        user_id: userInfo.userId,
        userName: userInfo.userName,
        summary_text: summaryResult.summary,
        date_generated: serverTimestamp(),
        ai_model_used: "Gemini 2.5 Pro",
        source_type: determinedSourceType,
        input_length: originalContent.length,
        subject: matchedSubject,
        key_points: summaryResult.keyPoints,
        topic: summaryResult.topic,
        sources: summaryResult.sources || [
          "AI-generated content - verify with authoritative sources",
        ],
        filename: sourceFileName || "", // ✅ FIX: Use empty string instead of undefined for Firebase compatibility
        student_notes: originalContent,
        student_notes_url: studentNotesUrl || "", // ✅ FIX: Use empty string instead of undefined when no file uploaded
      };

      logDebug("Summary record prepared for save", {
        topic: summaryRecord.topic,
        subject: summaryRecord.subject,
        source_type: summaryRecord.source_type,
        keyPointsCount: summaryRecord.key_points.length,
        sourcesCount: summaryRecord.sources.length,
        hasFile: !!studentNotesUrl,
        hasFilename: !!sourceFileName,
      });

      // Save to Firebase
      logInfo("📤 Saving summary to Firebase summaries collection...");
      const docRef = await addDoc(this.summariesCollection, summaryRecord);

      logger.timeEnd("Firebase Summary Save Process");
      logSuccess("✅ SUMMARY SAVED TO FIREBASE SUCCESSFULLY", {
        documentId: docRef.id,
        collection: "summaries",
        topic: summaryRecord.topic,
        subject: summaryRecord.subject,
        sessionId: finalSessionId,
        timestamp: new Date().toISOString(),
      });

      // Additional console log for visibility
      console.log("🎉 Summary Document Created:", {
        id: docRef.id,
        topic: summaryRecord.topic,
        subject: summaryRecord.subject,
      });

      return docRef.id;
    } catch (error) {
      logger.timeEnd("Firebase Summary Save Process");
      logError("Error saving summary to Firebase", error);
      throw new Error(
        `Failed to save summary: ${error instanceof Error ? error.message : "Unknown error"}`
      );
    }
  }

  /**
   * Wait for Firebase auth state to be ready
   */
  private async waitForAuthState(): Promise<void> {
    return new Promise((resolve) => {
      if (auth.currentUser) {
        logDebug("Auth user already available");
        resolve();
        return;
      }

      logDebug("Waiting for auth state");
      const unsubscribe = auth.onAuthStateChanged((user) => {
        logDebug("Auth state changed", { userExists: !!user });
        unsubscribe();
        resolve();
      });

      // Timeout after 5 seconds
      setTimeout(() => {
        logDebug("Auth state timeout reached");
        unsubscribe();
        resolve();
      }, 5000);
    });
  }

  /**
   * Get summaries for current student user
   */
  async getUserSummaries(limit?: number): Promise<SummaryRecord[]> {
    try {
      const userInfo = await this.getCurrentStudentUser();
      if (!userInfo) {
        throw new Error("Unable to identify current student user");
      }

      const summariesQuery = query(
        this.summariesCollection,
        where("user_id", "==", userInfo.userId)
      );

      const summariesSnapshot = await getDocs(summariesQuery);
      const summaries: SummaryRecord[] = [];

      summariesSnapshot.forEach((doc) => {
        summaries.push({
          summary_id: doc.id,
          ...(doc.data() as Omit<SummaryRecord, "summary_id">),
        });
      });

      // Sort by date_generated (most recent first)
      summaries.sort((a, b) => {
        const aTime = a.date_generated?.seconds || 0;
        const bTime = b.date_generated?.seconds || 0;
        return bTime - aTime;
      });

      // Apply limit if specified
      return limit ? summaries.slice(0, limit) : summaries;
    } catch (error) {
      logError("Error fetching user summaries", error);
      throw error;
    }
  }
}

// Lazy singleton instance to avoid circular dependency issues
let summaryServiceInstance: SummaryService | null = null;

export function getSummaryService(): SummaryService {
  if (!summaryServiceInstance) {
    summaryServiceInstance = new SummaryService();
  }
  return summaryServiceInstance;
}

// For backward compatibility, export a proxy that lazily initializes
export const summaryService = new Proxy({} as SummaryService, {
  get(target, prop) {
    return getSummaryService()[prop as keyof SummaryService];
  },
});
export default SummaryService;
